// src/pages/posters/components/poster-card.tsx
import { Card, CardContent } from "@/components/ui/card";
import { Edit2, Trash2, CheckCircle, XCircle, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { Poster } from "@/lib/api/types";
import { motion } from "framer-motion";
import { PermissionGuard } from "@/components/guards/PermissionGuard";

interface PosterCardProps {
  poster: Poster;
  onEdit: (poster: Poster) => void;
  onDelete: (poster: Poster) => void;
  onToggle: (poster: Poster) => void;
  onPreview: (poster: Poster) => void;
}

export function PosterCard({ poster, onEdit, onDelete, onToggle, onPreview }: PosterCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
    >
      <Card className="overflow-hidden h-full flex flex-col group hover:shadow-md transition-all duration-200 border-border/50 rounded-lg">
        {/* Poster Image */}
        <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-primary/10 to-primary/5">
          <img
            src={poster.imageUrl}
            alt={poster.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 cursor-pointer"
            onClick={() => onPreview(poster)}
          />

          {/* Status Badge */}
          <div className="absolute top-3 right-3">
            <Badge
              variant={poster.active ? 'default' : 'secondary'}
              className={`text-xs font-medium ${poster.active
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-gray-600 hover:bg-gray-700 text-white'
                }`}
            >
              {poster.active ? (
                <span className="flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Active
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <XCircle className="w-3 h-3" />
                  Inactive
                </span>
              )}
            </Badge>
          </div>
        </div>

        <CardContent className="p-5 flex-1 flex flex-col">
          {/* Title */}
          <h3 className="font-semibold text-lg mb-2 line-clamp-2 group-hover:text-primary transition-colors">
            {poster.title}
          </h3>

          {/* Date Info */}
          <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4 flex-wrap">
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {format(new Date(poster.startDate), "MMM d, yyyy")}
            </span>
            <span className="text-muted-foreground">→</span>
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {format(new Date(poster.endDate), "MMM d, yyyy")}
            </span>
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Actions */}
          <div className="flex gap-2 pt-4 border-t border-border">
            <PermissionGuard resource="posters" action="update">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(poster)}
                className="flex-1"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Edit
              </Button>
            </PermissionGuard>
            <PermissionGuard resource="posters" action="update">
              <Button
                variant={poster.active ? 'outline' : 'default'}
                size="sm"
                onClick={() => onToggle(poster)}
                className={`flex-1 ${poster.active
                    ? 'text-orange-600 hover:text-orange-700'
                    : 'text-green-600 hover:text-green-700 bg-transparent border-green-600 hover:bg-green-50'
                  }`}
              >
                {poster.active ? 'Deactivate' : 'Activate'}
              </Button>
            </PermissionGuard>
            <PermissionGuard resource="posters" action="delete">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDelete(poster)}
                className="hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </PermissionGuard>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}